import React, { memo, useCallback, useMemo } from 'react';
import { Box, IconButton, ListItem, Tooltip, makeStyles, useMediaQuery } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SpeedIcon from '@material-ui/icons/Speed';
import VisibilityIcon from '@material-ui/icons/Visibility';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import SyncAltSharpIcon from '@mui/icons-material/SyncAltSharp';
import ReorderIcon from '@mui/icons-material/Reorder';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import {
  stateTreeActionButtonStyle,
  stateTreeBadgeStyle,
  stateTreeCardBaseStyle,
  stateTreeGuideColor,
  themePalette,
} from '../Dashboards/State/stateTreeStyles';

const SELECTED_NODE_BACKGROUND = '#e0f2fe';
const EDIT_GROUP_PARENT_BACKGROUND = 'rgba(245, 158, 11, 0.18)';
const EDIT_GROUP_PARENT_BORDER = 'rgba(180, 83, 9, 0.42)';
const TREE_INDENT_SIZE = 20;
const TREE_GUIDE_BLEED = 3;
const TREE_GUIDE_ANCHOR_X = 12;
const TREE_GUIDE_ANCHOR_X_MOBILE = 10;
const ACTION_COLUMN_GAP = '1.35rem';
const ACTION_COLUMN_GAP_MOBILE = '0.92rem';
const INITIAL_ICON_GAP = '1.15rem';
const INITIAL_ICON_GAP_MOBILE = '0.88rem';
const DEVICE_ICON_GAP = '0.86rem';
const DEVICE_ICON_GAP_MOBILE = '0.64rem';
const TRAILING_ACTIONS_WIDTH = '9.2rem';
const TRAILING_ACTIONS_WIDTH_MOBILE = '7.1rem';

const useStyles = makeStyles(() => ({
  actionIcon: {
    fontSize: '1.125rem',
  },
  buttonReorderIdle: {
    padding: 3,
    borderRadius: '0.55rem',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    color: '#6b7280',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
  },
  editModeAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    borderRadius: '0.45rem',
    padding: '0.24rem 0.5rem',
    border: 'none',
    background: 'transparent',
    fontSize: '0.98rem',
    fontWeight: 700,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'color 160ms ease, background-color 160ms ease',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  editModeActionCancel: {
    color: '#ff2e2ec9',
    '&:hover': {
      color: '#ff2e2ec9',
      backgroundColor: 'rgba(255, 46, 46, 0.08)',
    },
  },
  editModeActionSave: {
    color: themePalette.status.online,
    '&:hover': {
      color: '#15803d',
      backgroundColor: 'rgba(34, 197, 94, 0.08)',
    },
  },
  editModeDivider: {
    width: '1px',
    height: '1.35rem',
    backgroundColor: 'rgba(161, 161, 170, 0.55)',
  },
}));

const normalizeNodeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const formatHierarchyBadgeLabel = (value) =>
  String(value || '')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const getNodeRowStyle = ({ isSelected, isDisabled, depth }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
  padding: depth === 0 ? '0.42rem 0.75rem' : '0.36rem 0.72rem',
  minHeight: '48px',
  cursor: isDisabled ? 'default' : 'pointer',
  backgroundColor: isSelected ? SELECTED_NODE_BACKGROUND : themePalette.common.white,
  opacity: isDisabled ? 0.64 : 1,
  boxSizing: 'border-box',
});

const getNodeItemStyle = ({ isSelected, depth, hasChildren, isEditingNode }) => ({
  ...stateTreeCardBaseStyle,
  borderColor: isEditingNode ? EDIT_GROUP_PARENT_BORDER : themePalette.border.subtle,
  backgroundColor: isEditingNode
    ? EDIT_GROUP_PARENT_BACKGROUND
    : isSelected
      ? SELECTED_NODE_BACKGROUND
      : hasChildren && depth > 0
        ? themePalette.surface.soft
        : themePalette.common.white,
  borderTopRightRadius: 0,
  borderBottomRightRadius: 0,
  boxSizing: 'border-box',
  boxShadow: isEditingNode
    ? 'inset 0 0 0 1px rgba(255, 248, 235, 0.7), 0 4px 10px rgba(180, 83, 9, 0.10)'
    : depth === 0
      ? themePalette.shadow.card
      : 'none',
});

export const getOrganizationTreeRowHeight = ({ isCompact }) => (isCompact ? 52 : 50);

function OrganizationTreeRow({
  item,
  selectedNodeId,
  onSelectNode,
  onToggleExpanded,
  onStartEdit,
  onSaveEditOrder,
  onCancelEditOrder,
  onAddNode,
  nodeStatus,
  isDemoPlan,
  isFreePlan,
  isMobile,
  totalLocationsCount,
  isEditingNode = false,
  editableChildrenCount = 0,
}) {
  const classes = useStyles();
  const { node, depth, hasChildren, isExpanded, hasNextSibling, ancestorHasNext } = item;
  const isSelected = selectedNodeId === node?.id;
  const isDisabled = !node?.owner;
  const hideHierarchyBadge = useMediaQuery('(max-width:600px)');
  const canEditPositions = !isMobile && (isEditingNode ? editableChildrenCount > 1 : node?.hijos?.length > 1);
  const canAddChild = !isDemoPlan && node?.level_nombre !== 'adicional';
  const normalizedLevelName = useMemo(() => normalizeNodeText(node?.level_nombre), [node?.level_nombre]);
  const childLabel = useMemo(() => {
    switch (normalizedLevelName) {
      case 'empresa':
        return 'zona';
      case 'zona':
        return 'localizacion';
      case 'localizacion':
        return 'sublocalizacion';
      case 'sublocalizacion':
        return 'extra';
      case 'extra':
        return 'adicional';
      default:
        return 'nodo';
    }
  }, [normalizedLevelName]);
  const isFreeLocationLimitReached = isFreePlan && normalizedLevelName === 'zona' && totalLocationsCount >= 1;
  const addDisabled = isFreeLocationLimitReached;
  const addTooltip = addDisabled
    ? 'El plan Gratuito solo permite una localizacion.'
    : `Presione para crear una ${childLabel}`;
  const levelBadgeLabel = useMemo(() => formatHierarchyBadgeLabel(node?.level_nombre), [node?.level_nombre]);
  const guideGutterWidth = depth * TREE_INDENT_SIZE;
  const actionIconButtonStyle = useMemo(
    () => ({
      padding: 0,
      minWidth: 0,
      color: themePalette.text.disabled,
    }),
    []
  );
  const rowStyle = useMemo(
    () => ({
      ...getNodeRowStyle({ isSelected, isDisabled, depth }),
      ...getNodeItemStyle({ isSelected, depth, hasChildren, isEditingNode }),
      alignItems: isMobile ? 'flex-start' : 'center',
      padding: isMobile
        ? depth === 0
          ? '0.42rem 0.62rem'
          : '0.38rem 0.58rem'
        : depth === 0
          ? '0.42rem 0.75rem'
          : '0.36rem 0.72rem',
      paddingLeft: isMobile
        ? depth === 0
          ? 'calc(0.62rem + 7px)'
          : 'calc(0.58rem + 7px)'
        : depth === 0
          ? 'calc(0.75rem + 7px)'
          : 'calc(0.72rem + 7px)',
    }),
    [depth, hasChildren, isDisabled, isEditingNode, isMobile, isSelected]
  );
  const sharedIconButtonStyle = useMemo(
    () => ({
      ...stateTreeActionButtonStyle,
      padding: 0,
      width: '1.7rem',
      height: '1.7rem',
      minWidth: '1.7rem',
    }),
    []
  );
  const actionColumnGap = isMobile ? ACTION_COLUMN_GAP_MOBILE : ACTION_COLUMN_GAP;
  const initialIconGap = isMobile ? INITIAL_ICON_GAP_MOBILE : INITIAL_ICON_GAP;
  const deviceIconGap = isMobile ? DEVICE_ICON_GAP_MOBILE : DEVICE_ICON_GAP;
  const trailingActionsWidth = isMobile ? TRAILING_ACTIONS_WIDTH_MOBILE : TRAILING_ACTIONS_WIDTH;
  const treeGuideAnchorX = isMobile ? TREE_GUIDE_ANCHOR_X_MOBILE : TREE_GUIDE_ANCHOR_X;

  const handleRowClick = useCallback(() => {
    if (isDisabled) return;
    onSelectNode(node);
  }, [isDisabled, node, onSelectNode]);

  const handleAction = useCallback((event, callback) => {
    event.stopPropagation();
    callback?.();
  }, []);

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        paddingLeft: `${guideGutterWidth}px`,
        boxSizing: 'border-box',
      }}
    >
      {ancestorHasNext.slice(1).map((ancestorKeepsBranchOpen, index) =>
        ancestorKeepsBranchOpen ? (
          <Box
            key={`organization-ancestor-guide-${node?.id}-${index}`}
            aria-hidden
            style={{
              position: 'absolute',
              left: `${index * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
              top: `-${TREE_GUIDE_BLEED}px`,
              bottom: `-${TREE_GUIDE_BLEED}px`,
              width: '1px',
              backgroundColor: stateTreeGuideColor,
              pointerEvents: 'none',
            }}
          />
        ) : null
      )}

      {depth > 0 && (
        <>
          <Box
            aria-hidden
            style={{
              position: 'absolute',
              left: `${(depth - 1) * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
              top: `-${TREE_GUIDE_BLEED}px`,
              bottom: hasNextSibling ? `-${TREE_GUIDE_BLEED}px` : '50%',
              width: '1px',
              backgroundColor: stateTreeGuideColor,
              pointerEvents: 'none',
            }}
          />

          <Box
            aria-hidden
            style={{
              position: 'absolute',
              left: `${(depth - 1) * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
              top: '50%',
              width: `${TREE_INDENT_SIZE - treeGuideAnchorX}px`,
              height: '1px',
              backgroundColor: stateTreeGuideColor,
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
        </>
      )}

      {hasChildren && isExpanded && (
        <Box
          aria-hidden
          style={{
            position: 'absolute',
            left: `${depth * TREE_INDENT_SIZE + treeGuideAnchorX}px`,
            top: 'calc(100% - 1px)',
            bottom: `-${TREE_GUIDE_BLEED}px`,
            width: '1px',
            backgroundColor: stateTreeGuideColor,
            pointerEvents: 'none',
          }}
        />
      )}

      <ListItem
        component='div'
        button
        selected={isSelected}
        onClick={handleRowClick}
        aria-disabled={isDisabled}
        style={rowStyle}
      >
        <Box
          style={{
            minWidth: 0,
            flex: 1,
            display: 'flex',
            alignItems: isMobile ? 'flex-start' : 'center',
            gap: isMobile ? '0.4rem' : '0.55rem',
            overflow: 'hidden',
          }}
        >
          {!node?.owner && (
            <Tooltip title={`El nodo ${node?.nombre} es compartido`}>
              <IconButton
                edge='end'
                aria-label='nodo compartido'
                size='small'
                style={sharedIconButtonStyle}
              >
                <VisibilityIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}

          <Box
            style={{
              flexShrink: 0,
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: node?.color || themePalette.primary.main,
              border: `2px solid ${themePalette.common.white}`,
              boxShadow: `0 0 0 1px ${themePalette.border.main}, 0 3px 8px rgba(15, 23, 42, 0.08)`,
            }}
          />

          {!hideHierarchyBadge && (
            <span
              style={{
                ...stateTreeBadgeStyle,
                fontSize: isMobile ? '0.68rem' : stateTreeBadgeStyle.fontSize,
                padding: isMobile ? '0.1rem 0.35rem' : stateTreeBadgeStyle.padding,
                backgroundColor:
                  normalizedLevelName === 'empresa'
                    ? 'rgba(8, 95, 145, 0.08)'
                    : stateTreeBadgeStyle.backgroundColor,
                borderColor:
                  normalizedLevelName === 'empresa'
                    ? 'rgba(8, 95, 145, 0.18)'
                    : stateTreeBadgeStyle.borderColor,
                color:
                  normalizedLevelName === 'empresa'
                    ? themePalette.text.section
                    : stateTreeBadgeStyle.color,
              }}
            >
              {levelBadgeLabel}
            </span>
          )}

          <Box
            style={{
              minWidth: 0,
              flex: 1,
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.35rem',
              flexWrap: 'wrap',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                fontSize: isMobile ? '0.88rem' : depth === 0 ? '1rem' : '0.96rem',
                fontWeight: depth === 0 ? 500 : 400,
                color: themePalette.primary.main,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {node?.nombre}
            </span>
          </Box>
        </Box>

        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: actionColumnGap,
            flexWrap: 'nowrap',
            flexShrink: 0,
            paddingTop: isMobile ? 2 : 0,
            paddingRight: isMobile ? 'calc(0.18rem + 7px)' : 'calc(0.3rem + 7px)',
          }}
        >
          <Box
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: initialIconGap,
            }}
          >
            {node.es_generador && (
              <Tooltip title='Usuario generador'>
                <IconButton
                  edge='end'
                  aria-label='es generador'
                  style={actionIconButtonStyle}
                  onClick={(event) => event.stopPropagation()}
                >
                  <WbSunnyIcon className={classes.actionIcon} style={{ color: '#F59500' }} />
                </IconButton>
              </Tooltip>
            )}

            {node.es_bidireccional && (
              <Tooltip title='Bidireccional'>
                <IconButton
                  edge='end'
                  aria-label='bidireccional'
                  style={actionIconButtonStyle}
                  onClick={(event) => event.stopPropagation()}
                >
                  <SyncAltSharpIcon className={classes.actionIcon} style={{ color: '#0000008a' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {canEditPositions && isEditingNode && (
            <Box
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: isMobile ? '0.55rem' : '0.9rem',
                marginLeft: 'auto',
              }}
            >
              <button
                type='button'
                className={`${classes.editModeAction} ${classes.editModeActionCancel}`}
                disabled={isDemoPlan}
                onClick={(event) => handleAction(event, onCancelEditOrder)}
              >
                <CloseIcon className={classes.actionIcon} style={{ fontSize: '1rem' }} />
                <span>Cancelar</span>
              </button>
              <Box className={classes.editModeDivider} aria-hidden />
              <button
                type='button'
                className={`${classes.editModeAction} ${classes.editModeActionSave}`}
                disabled={isDemoPlan}
                onClick={(event) => handleAction(event, onSaveEditOrder)}
              >
                <CheckIcon className={classes.actionIcon} style={{ fontSize: '1rem' }} />
                <span>Guardar</span>
              </button>
            </Box>
          )}

          <Box
            style={{
              width: trailingActionsWidth,
              minWidth: trailingActionsWidth,
              display: canEditPositions && isEditingNode ? 'none' : 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: actionColumnGap,
            }}
          >
            <Box
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: deviceIconGap,
              }}
            >
              {node?.automates?.length > 0 && (
                <Tooltip title={`${node.nombre} tiene un automate asociado`}>
                  <IconButton
                    edge='end'
                    aria-label='automates asociados'
                    style={actionIconButtonStyle}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <PrecisionManufacturingIcon fontSize='small' style={{ color: themePalette.text.disabled }} />
                  </IconButton>
                </Tooltip>
              )}

              {node?.medidor && (
                <Tooltip title={`${node.nombre} tiene un medidor asociado ${nodeStatus?.online ? 'online' : 'offline'}`}>
                  <IconButton
                    edge='end'
                    aria-label='medidores asociados'
                    style={actionIconButtonStyle}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <SpeedIcon
                      fontSize='small'
                      style={{
                        color: nodeStatus?.online
                          ? themePalette.status.online
                          : themePalette.status.offline,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}

              {canEditPositions && !isEditingNode && (
                <Tooltip title='Cambiar orden de nodos'>
                  <IconButton
                    edge='end'
                    disabled={isDemoPlan}
                    className={classes.buttonReorderIdle}
                    style={{
                      marginLeft: isMobile ? '0.18rem' : '1rem',
                      marginRight: isMobile ? '0.08rem' : 0,
                    }}
                    onClick={(event) => handleAction(event, () => onStartEdit(node))}
                  >
                    <ReorderIcon className={classes.actionIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {canAddChild && (
              <Tooltip title={addTooltip} arrow placement='right'>
                <span>
                  <IconButton
                    edge='end'
                    aria-label='agregar nodo'
                    disabled={addDisabled}
                    style={{
                      ...actionIconButtonStyle,
                      marginRight: isMobile ? '0.18rem' : '0.28rem',
                    }}
                    onClick={(event) => handleAction(event, () => onAddNode(node))}
                  >
                    <AddIcon className={classes.actionIcon} />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {hasChildren &&
              (isExpanded ? (
                <Tooltip title='Contraer'>
                  <IconButton
                    edge='end'
                    aria-label='contraer'
                    onClick={(event) => handleAction(event, () => onToggleExpanded(node.id))}
                    style={actionIconButtonStyle}
                  >
                    <ExpandLessIcon className={classes.actionIcon} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title='Expandir'>
                  <IconButton
                    edge='end'
                    aria-label='expandir'
                    onClick={(event) => handleAction(event, () => onToggleExpanded(node.id))}
                    style={actionIconButtonStyle}
                  >
                    <ExpandMoreIcon className={classes.actionIcon} />
                  </IconButton>
                </Tooltip>
              ))}
          </Box>
        </Box>
      </ListItem>
    </Box>
  );
}

export default memo(OrganizationTreeRow);
